import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  booleanAttribute,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';
import { DropdownMenuItem, DropdownMenuKind } from './dropdown-types';

/**
 * The standalone popover surface of the dropdown — a `surface-container-lowest`
 * card at `radius-md`/elevation-2 holding a 4px-padded stack of 48px rows (icon,
 * label, optional inline supporting text). It serves two ARIA contracts: an
 * action `menu` (rows are `menuitem`s) and a select `listbox` (rows are
 * `option`s with `aria-selected`).
 *
 * The consumer owns visibility and positioning (render it inline, or inside a
 * CDK overlay). `compsych-dropdown` composes the same visual language for the
 * panel it opens; this component exposes it for standalone action menus.
 */
@Component({
  selector: 'compsych-dropdown-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="cs-menu"
      [class.compact]="compact"
      [attr.role]="kind === 'listbox' ? 'listbox' : 'menu'"
      [attr.aria-label]="ariaLabel || null"
    >
      @for (item of items; track item.id ?? item.label) {
        <button
          type="button"
          class="cs-menu__item"
          [class.error]="!!item.error"
          [attr.role]="kind === 'listbox' ? 'option' : 'menuitem'"
          [attr.aria-selected]="kind === 'listbox' ? (item.id ?? item.label) === selectedId : null"
          [disabled]="!!item.disabled"
          (click)="select(item)"
        >
          <span class="cs-menu__inner">
            @if (item.icon || leadingIcon) {
              <span class="cs-menu__icon" aria-hidden="true" [innerHTML]="iconSvg(item.icon || leadingIcon)"></span>
            }
            <span class="cs-menu__text">
              <span class="cs-menu__label">{{ item.label }}</span>
              @if (item.supportingText) {
                <span class="cs-menu__support">{{ item.supportingText }}</span>
              }
            </span>
          </span>
        </button>
      }
    </div>
  `,
  styleUrl: './dropdown-menu.css',
})
export class DropdownMenuComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** Rows to render. */
  @Input() items: DropdownMenuItem[] = [];
  /** ARIA contract: `'menu'` (default, action menu) or `'listbox'` (select). */
  @Input() kind: DropdownMenuKind = 'menu';
  /** In `listbox` mode, the id (or label) of the currently-selected row. */
  @Input() selectedId?: string;
  /** Default Lucide icon shown before every row's label; each item's own `icon` (from the Icon Mapping) overrides it. */
  @Input() leadingIcon?: string;
  /** Denser variant — one type-ramp step down, rows hug to 44px. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) compact = false;
  /**
   * Accessible name for the list. Required in `listbox` mode when no external
   * labelled control owns the menu (a nameless listbox fails accessibility checks).
   */
  @Input() ariaLabel?: string;

  /** Emits the chosen row (disabled rows never emit). */
  @Output() readonly itemSelect = new EventEmitter<DropdownMenuItem>();

  private readonly iconCache = new Map<string, SafeHtml>();

  protected iconSvg(name: string | undefined): SafeHtml {
    const key = name ?? '';
    let svg = this.iconCache.get(key);
    if (!svg) {
      const raw = (LUCIDE_ICONS[key] ?? '').replace('<svg ', '<svg width="20" height="20" ');
      svg = this.sanitizer.bypassSecurityTrustHtml(raw);
      this.iconCache.set(key, svg);
    }
    return svg;
  }

  protected select(item: DropdownMenuItem): void {
    if (item.disabled) return;
    this.itemSelect.emit(item);
  }
}
